import React, { useState, useEffect } from "react";
import { Box, Paper, TextField, Typography, Snackbar, Alert } from "@mui/material";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import PaymentIcon from "@mui/icons-material/Payment";
import { getData, postData, API_ENDPOINTS } from "../../services/apiService.js";
import LoadingSpinner from "../LoadingContext";
import { useNavigate } from "react-router-dom";
import "./Settings.css"

function BillingSettings() {
    const [platformFee, setPlatformFee] = useState("");
    const [gstFee, setGstFee] = useState("");
    const [editModePlatform, setEditModePlatform] = useState(false);
    const [editModeGst, setEditModeGst] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const navigate = useNavigate();

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    // Formatter for fees
    const formatFee = (value) => {
        if (value === null || value === undefined || value === "") return "";
        const num = parseFloat(value);
        if (isNaN(num)) return "";
        return Number.isInteger(num) ? num.toString() : parseFloat(num.toFixed(2)).toString();
    };

    // Fetch initial charges
    useEffect(() => {
        const fetchCharges = async () => {
            setLoading(true);
            try {
                const res = await getData(API_ENDPOINTS.getAllCharges);

                if (res?.statusCode === 200) {
                    const charges = res.message.data;
                    const platform = charges.find((c) => c.key === "platform_fee");
                    const gst = charges.find((c) => c.key === "gst_percent");

                    if (platform) setPlatformFee(formatFee(platform.value_numeric));
                    if (gst) setGstFee(formatFee(gst.value_numeric));
                } else {
                    setSnackbar({
                        open: true,
                        message: "Failed to fetch billing data",
                        severity: "error",
                    });
                }
            } catch (error) {
                console.error("Error fetching charges:", error);
                setSnackbar({
                    open: true,
                    message: "Failed to fetch billing data",
                    severity: "error",
                });
            } finally {
                setLoading(false);
            }
        };

        fetchCharges();
    }, []);

    // Save function
    const handleSave = async (key, value, setEditMode) => {
        setSaving(true);
        try {
            const existingChargesRes = await getData(API_ENDPOINTS.getAllCharges);

            const charges = existingChargesRes?.statusCode === 200
                ? existingChargesRes.message.data || []
                : [];

            const existingCharge = charges.find((c) => c.key === key);

            let res;
            if (existingCharge) {
                const payload = { key, value_numeric: parseFloat(value) };
                res = await postData(API_ENDPOINTS.updateCharge, payload);
            } else {
                const payload = { key, value_numeric: parseFloat(value), value_text: "" };
                res = await postData(API_ENDPOINTS.generateCharges, payload);
            }

            if (res.statusCode === 200) {
                setSnackbar({
                    open: true,
                    message: `${key} saved successfully`,
                    severity: "success",
                });
                setEditMode(false);
            } else {
                setSnackbar({
                    open: true,
                    message: `Failed to save ${key}`,
                    severity: "error",
                });
            }
        } catch (error) {
            console.error("Error saving charge:", error);
            setSnackbar({
                open: true,
                message: "Something went wrong!",
                severity: "error",
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="billing-settings">
            <LoadingSpinner isLoading={loading || saving} />
            <Box className="d-flex gap-3 mb-3" alignItems="center">
                <img
                    src="/smart-vectors/Back.svg"
                    alt="back"
                    style={{ width: 32, height: 32, cursor: "pointer", marginTop: 3, marginRight: 12 }}
                    onClick={() => navigate(`/app/settings`)}
                />
                <Box>
                    <Typography variant="h5" fontWeight={600} fontSize={19}>
                        Billing & Pricing Configuration
                    </Typography>
                    <Typography variant="body2" fontSize={14}>Configure pricing rules and billing settings</Typography>
                </Box>
            </Box>

            <Box mt={2} display="flex" flexWrap="wrap" gap={2}>
                {/* Platform Fee Section */}
                <Paper
                    elevation={0}
                    sx={{
                        p: 2,
                        flex: { xs: "1 1 100%", md: "1 1 45%" },
                        borderRadius: "16px",
                        border: "1px solid #E0E0E0",
                    }}
                >
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <RequestQuoteIcon sx={{ color: "primary.main", fontSize: 20 }} />
                        <Box display="flex" justifyContent="space-between" flex={1}>
                            <Typography variant="subtitle1" fontWeight={600}>
                                Platform Fee
                            </Typography>
                            <div className="edit-container">
                                {!editModePlatform ? (
                                    <img
                                        src="/smart-vectors/Edit-Profile.svg"
                                        alt="Edit"
                                        style={{ width: 18, height: 18, cursor: "pointer", marginLeft: 10, marginTop: 5 }}
                                        onClick={() => setEditModePlatform(true)}
                                    />
                                ) : (
                                    <img
                                        src="/smart-vectors/Save.svg"
                                        alt="Save"
                                        style={{ width: 18, height: 18, cursor: "pointer", marginLeft: 10, marginTop: 5 }}
                                        onClick={() => handleSave("platform_fee", platformFee, setEditModePlatform)}
                                    />
                                )}
                            </div>
                        </Box>
                    </Box>

                    <Typography variant="body2" color="text.secondary" mb={1}>
                        Define the service fee charged by the platform.
                    </Typography>

                    <TextField
                        id="platform-fee"
                        variant="outlined"
                        value={platformFee}
                        onChange={(e) => setPlatformFee(e.target.value)}
                        InputProps={{ readOnly: !editModePlatform, endAdornment: <span style={{ marginLeft: 4 }}>%</span> }}
                        sx={{
                            width: "300px",
                            "& .MuiInputBase-root": { height: "40px" },
                            "& .MuiInputBase-input": { height: "40px", padding: "0 8px", boxSizing: "border-box" },
                        }}
                    />
                </Paper>

                {/* GST Section */}
                <Paper
                    elevation={0}
                    sx={{
                        p: 2,
                        flex: { xs: "1 1 100%", md: "1 1 45%" },
                        borderRadius: "16px",
                        border: "1px solid #E0E0E0",
                    }}
                >
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <PaymentIcon sx={{ color: "success.main", fontSize: 20 }} />
                        <Box display="flex" justifyContent="space-between" flex={1}>
                            <Typography variant="subtitle1" fontWeight={600}>
                                GST (%)
                            </Typography>
                            <div className="edit-container">
                                {!editModeGst ? (
                                    <img
                                        src="/smart-vectors/Edit-Profile.svg"
                                        alt="Edit"
                                        style={{ width: 18, height: 18, cursor: "pointer", marginLeft: 10, marginTop: 5 }}
                                        onClick={() => setEditModeGst(true)}
                                    />
                                ) : (
                                    <img
                                        src="/smart-vectors/Save.svg"
                                        alt="Save"
                                        style={{ width: 18, height: 18, cursor: "pointer", marginLeft: 10, marginTop: 5 }}
                                        onClick={() => handleSave("gst_percent", gstFee, setEditModeGst)}
                                    />
                                )}
                            </div>
                        </Box>
                    </Box>

                    <Typography variant="body2" color="text.secondary" mb={1}>
                        Set the applicable Goods and Services Tax percentage.
                    </Typography>

                    <TextField
                        id="gst-fee"
                        variant="outlined"
                        value={gstFee}
                        onChange={(e) => setGstFee(e.target.value)}
                        InputProps={{ readOnly: !editModeGst, endAdornment: <span style={{ marginLeft: 4 }}>%</span> }}
                        sx={{
                            width: "300px",
                            "& .MuiInputBase-root": { height: "40px" },
                            "& .MuiInputBase-input": { height: "40px", padding: "0 8px", boxSizing: "border-box" },
                        }}
                    />
                </Paper>
            </Box>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default BillingSettings;